import axios, { type AxiosInstance } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { mockApi } from '@/mock/mockAdapter';

const IS_MOCK = import.meta.env.VITE_MOCK === 'true';
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// In mock mode, use the mock adapter instead of real axios
export const api = (IS_MOCK
  ? mockApi
  : axios.create({
      baseURL: API_BASE,
      timeout: 15000,
      headers: { 'Content-Type': 'application/json' },
    })) as AxiosInstance;

/**
 * Safely extract the payload from an API response.
 * The backend wraps responses in { data: ... } – this unwraps it.
 */
function unwrap(body: unknown): unknown {
  if (body && typeof body === 'object' && !Array.isArray(body) && 'data' in (body as Record<string, unknown>)) {
    return (body as Record<string, unknown>).data;
  }
  return body;
}

/**
 * Ensure a value is an array. Handles all defensive cases:
 * - already an array → return as-is
 * - wrapped in { data: [...] } → unwrap and return
 * - anything else → return []
 */
function ensureArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value;
  const inner = unwrap(value);
  if (Array.isArray(inner)) return inner;
  return [];
}

// Generic fetch hook – unwraps the API's { data: ... } envelope
function useFetch<T>(key: string[], url: string, options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery<T>({
    queryKey: key,
    queryFn: async () => {
      const res = await api.get(url);
      return unwrap(res.data) as T;
    },
    ...options,
  });
}

// Fetch hook for endpoints that return arrays – guarantees T[] even if API returns unexpected shapes
function useFetchArray<T>(key: string[], url: string, options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery<T[]>({
    queryKey: key,
    queryFn: async () => {
      const res = await api.get(url);
      return ensureArray<T>(res.data);
    },
    ...options,
  });
}

// Public API hooks - no auth required

export function useAnnouncements(page = 1, category?: string) {
  const params = new URLSearchParams({ page: String(page) });
  if (category && category !== 'all') params.set('category', category);
  return useQuery<{ items: Announcement[]; total: number; page: number; pages: number }>({
    queryKey: ['announcements', String(page), category || 'all'],
    queryFn: async () => {
      const { data: body } = await api.get(`/announcements?${params}`);
      return {
        items: body.data ?? [],
        total: body.total ?? 0,
        page: body.page ?? 1,
        pages: body.totalPages ?? 1,
      };
    },
  });
}

export function useAnnouncement(id: string) {
  return useFetch<Announcement>(['announcement', id], `/announcements/${id}`, { enabled: !!id });
}

export function useCompetitions() {
  return useFetchArray<Competition>(['competitions'], '/competitions');
}

export function useCompetition(id: string) {
  return useFetch<Competition>(['competition', id], `/competitions/${id}`, { enabled: !!id });
}

export function useLiveScores(competitionId?: string) {
  const url = competitionId ? `/results/live?competitionId=${competitionId}` : '/results/live';
  return useFetchArray<ScoreEntry>(
    ['liveScores', competitionId || 'all'],
    url,
    { refetchInterval: 10000 },
  );
}

export function useMedalTally() {
  return useFetchArray<MedalTallyEntry>(['medalTally'], '/results/medals', { refetchInterval: 30000 });
}

export function useSchedule() {
  return useFetchArray<ScheduleEntry>(['schedule'], '/schedule');
}

export function useVenues() {
  return useFetchArray<Venue>(['venues'], '/venues');
}

export function useVenue(id: string) {
  return useFetch<Venue>(['venue', id], `/venues/${id}`, { enabled: !!id });
}

export function useStats() {
  return useFetch<StatsData>(['stats'], '/stats', { refetchInterval: 60000 });
}

export function useMedia(type?: 'photo' | 'video') {
  const params = type ? `?type=${type}` : '';
  return useFetchArray<MediaItem>(['media', type || 'all'], `/media${params}`);
}

// SSE hook for truly live scores
export function useScoreSSE(onMessage: (data: ScoreEvent) => void) {
  const connect = () => {
    const es = new EventSource(`${API_BASE}/results/live/stream`);
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as ScoreEvent;
        onMessage(data);
      } catch {
        // ignore parse errors
      }
    };
    es.onerror = () => {
      es.close();
      setTimeout(connect, 5000);
    };
    return es;
  };
  return connect;
}

// Types
export interface Announcement {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  category: string;
  imageUrl?: string;
  author: string;
  createdAt: string;
  updatedAt: string;
}

export interface Competition {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  rules: string;
  ageBracket: string;
  gender: 'male' | 'female' | 'mixed' | 'open';
  venue: string;
  registeredTeams: number;
  participants: number;
}

export interface ScoreEntry {
  id: string;
  competitionId: string;
  competitionName: string;
  rank: number;
  athleteName: string;
  township: string;
  result: string;
  status: 'notStarted' | 'live' | 'completed' | 'upcoming';
}

export interface ScoreEvent {
  type: 'update' | 'final';
  competitionId: string;
  scores: ScoreEntry[];
}

export interface MedalTallyEntry {
  township: string;
  gold: number;
  silver: number;
  bronze: number;
  total: number;
}

export interface ScheduleEntry {
  id: string;
  day: 1 | 2 | 3;
  startTime: string;
  endTime: string;
  title: string;
  titleEn: string;
  venue: string;
  category: 'opening' | 'competition' | 'closing' | 'athletesNight' | 'ceremony' | 'other';
}

export interface Venue {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  address: string;
  capacity: number;
  imageUrl?: string;
  mapUrl?: string;
  competitions: string[];
  facilities: string[];
}

export interface StatsData {
  totalParticipants: number;
  totalTownships: number;
  totalCompetitions: number;
  totalMedals: number;
  participationByTownship: { name: string; count: number }[];
  participationByCompetition: { name: string; count: number }[];
  medalDistribution: { township: string; gold: number; silver: number; bronze: number }[];
  registrationTrend: { date: string; count: number }[];
}

export interface MediaItem {
  id: string;
  type: 'photo' | 'video';
  title: string;
  url: string;
  thumbnailUrl: string;
  createdAt: string;
}
